import React, { useCallback, useEffect, useState } from 'react';
import { FieldType } from './field.types';
import { useFormValidation } from '../context/FormValidation';
import ReactPhoneInput, { CountryData } from 'react-phone-input-material-ui';
import { TextField } from '@mui/material';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useSingleForm } from '../context/SingleForm';

type PhoneNumberProps = FieldType & {
  useGlobalValidation: boolean;
  errorMessage?: string;
};

export const PhoneNumberField: React.FC<PhoneNumberProps> = ({
  fieldKey,
  name,
  defaultValue,
  required,
  onChangeFieldValue,
  useGlobalValidation = true,
  errorMessage,
}) => {
  const [value, setValue] = useState<string>(defaultValue || '');
  const [isValid, setIsValid] = useState<boolean>(true);
  const [hasBlurred, setHasBlurred] = useState<boolean>(false);
  const [isLocalNumber, setIsLocalNumber] = useState<boolean>(false);
  const { updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();
  const isError = !isValid && hasBlurred;
  const helperText = isError ? errorMessage : '';

  useEffect(() => {
    if (required && useGlobalValidation) {
      const isValid = defaultValue !== undefined && defaultValue.length > 0;
      updateFieldValidation(`${formIndex}-${fieldKey}`, isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback(
    (value: string, data: CountryData) => {
      setValue(value);
      let newValue = value;
      if (value.startsWith('00')) {
        setIsLocalNumber(false);
        setValue(`+${value.substring(2)}`);
        newValue = `+${value.substring(2)}`;
      } else if (value.startsWith('0') && value.length === 1) {
        setIsLocalNumber(true);
        newValue = '+41';
      } else if (value.startsWith('0') && value.length > 1) {
        setIsLocalNumber(false);
        newValue = `+41${value.substring(1)}`;
        setValue(newValue);
      } else {
        setIsLocalNumber(false);
        setValue(value);
      }

      if (!newValue.startsWith('+')) {
        newValue = `+${newValue}`;
      }

      const isValid = isValidPhoneNumber(newValue, data.countryCode.toUpperCase() as CountryCode);
      setIsValid(isValid);
      if (useGlobalValidation) {
        updateFieldValidation(`${formIndex}-${fieldKey}`, isValid);
      }
      onChangeFieldValue(fieldKey, newValue);
    },
    [onChangeFieldValue, fieldKey, formIndex, useGlobalValidation, updateFieldValidation]
  );

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, data: CountryData) => {
      setHasBlurred(true);
      const isValid = isValidPhoneNumber(event.target.value, data.countryCode.toUpperCase() as CountryCode);
      setIsValid(isValid);
      updateFieldValidation(`${formIndex}-${fieldKey}`, isValid);
    },
    [formIndex, fieldKey, updateFieldValidation]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.nativeEvent.key === '+' && value.length === 0) {
        setIsLocalNumber(false);
        setValue('+');
      }
    },
    [value]
  );

  const handleFocus = useCallback((event: any) => {
    // Skip selection everything when user first time focus on input
    if (event.target.value === '+41') {
      const value = event.target.value;
      event.target.value = '';
      event.target.value = value;
    }
  }, []);

  if (isLocalNumber) {
    return (
      <ReactPhoneInput
        label={name}
        country="ch"
        prefix=""
        autoFormat={false}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        component={TextField}
        inputProps={{
          id: `${formIndex}-${fieldKey}`,
          name: fieldKey,
          required,
          onFocus: handleFocus,
          error: isError,
          helperText,
        }}
      />
    );
  }

  return (
    <ReactPhoneInput
      label={name}
      country="ch"
      prefix="+"
      placeholder="+41 78 123 45 67"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      component={TextField}
      inputProps={{
        id: `${formIndex}-${fieldKey}`,
        name: fieldKey,
        required,
        onFocus: handleFocus,
        error: isError,
        helperText,
      }}
    />
  );
};
